import {css} from 'aphrodite';
import React, {useRef} from 'react';
import _ from 'underscore';

import {ProcessExecutionType, ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  processExecutionTypeDisplayName,
  getEnabledProcessExecutionTypesForPlatformFeature,
} from 'processes/ProcessConstants';
import {sharedStyles} from 'processes/settings/fields/ProcessTaskSchemaFieldConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SettingsField, {SettingsPopover} from 'ui-library/SettingsField';

export const EXECUTION_TYPE_FIELD_HELP_TEXT =
  'Configure tasks to be executed either with a notebook entry, directly, or with a flowchart.';

export default function ExecutionTypeField(props: {
  value: ProcessExecutionType;
  isBeingCreated: boolean;
  onChange: (executionType: ProcessExecutionType) => void;
}): JSX.Element {
  const idRef = useRef(_.uniqueId('executionType'));

  const executionType = props.value;
  // this component is only valid for process client origin, so we can just hardcode it
  const clientOrigin = ProcessClientOrigin.PROCESSES;

  return props.isBeingCreated ? (
    <InputWrapper
      id={idRef.current}
      label="Execution type"
      labelStyle={FieldLabel.STYLES.LABEL}
      isRequired={true}
      help={EXECUTION_TYPE_FIELD_HELP_TEXT}
    >
      <SelectDropdown
        displayName={processExecutionTypeDisplayName(clientOrigin, executionType)}
        className={css(sharedStyles.input)}
        menu={
          <Menu value={executionType} width={MenuWidth.FULL}>
            {getEnabledProcessExecutionTypesForPlatformFeature(ProcessPlatformFeatureType.SCHEMA_CONFIG)
              .sort((a, b) => a.localeCompare(b))
              .map((executionType) => (
                <Option
                  key={executionType}
                  value={executionType}
                  onSelect={() => {
                    props.onChange(executionType);
                  }}
                  dataTestAttrs={{component: 'ExecutionTypeField', element: 'option', key: executionType}}
                >
                  {processExecutionTypeDisplayName(clientOrigin, executionType)}
                </Option>
              ))}
          </Menu>
        }
        dataTestAttrs={{component: 'ExecutionTypeField', element: 'root'}}
      />
    </InputWrapper>
  ) : (
    <SettingsField
      label="Execution type"
      name="Execution type"
      value={processExecutionTypeDisplayName(clientOrigin, executionType)}
      isBeingCreated={false}
      isRequired={false}
      isWritable={false}
      onChange={() => {}}
      dataTestAttrs={{component: 'ExecutionTypeField', element: 'root'}}
      helpIcon={
        <SettingsPopover
          renderContent={() => (
            <div className={css(sharedStyles.helpTooltip)}>{EXECUTION_TYPE_FIELD_HELP_TEXT}</div>
          )}
        />
      }
    />
  );
}
